import { useEffect, useState } from 'react';

/**
 * Feature flags to control the rollout of new features
 */
const FLAGS = {
  MOBILE_UI_IMPROVEMENTS: 'mobile-ui-improvements',
  RESPONSIVE_MESSAGING: 'responsive-messaging',
  IMPROVED_HAMBURGER: 'improved-hamburger-menu',
};

// Default values for each flag
const DEFAULT_FLAGS: Record<string, boolean> = {
  [FLAGS.MOBILE_UI_IMPROVEMENTS]: true,
  [FLAGS.RESPONSIVE_MESSAGING]: true,
  [FLAGS.IMPROVED_HAMBURGER]: true,
};

/**
 * Attempts to load feature flags from localStorage
 * Falls back to defaults if not found
 */
const loadFlags = (): Record<string, boolean> => {
  try {
    const saved = localStorage.getItem('feature_flags');
    return saved ? { ...DEFAULT_FLAGS, ...JSON.parse(saved) } : DEFAULT_FLAGS;
  } catch (error) {
    console.error('Error loading feature flags', error);
    return DEFAULT_FLAGS;
  }
};

/**
 * Saves feature flags to localStorage
 */
const saveFlags = (flags: Record<string, boolean>): void => {
  try {
    localStorage.setItem('feature_flags', JSON.stringify(flags));
  } catch (error) {
    console.error('Error saving feature flags', error);
  }
};

/**
 * Hook to check if a feature flag is enabled
 */
export const useFeatureFlag = (flagName: string): boolean => {
  const [flags, setFlags] = useState<Record<string, boolean>>(loadFlags);

  // Only reload on component mount
  useEffect(() => {
    setFlags(loadFlags());
  }, []);

  return flags[flagName] ?? DEFAULT_FLAGS[flagName] ?? false;
};

/**
 * Admin function to enable a feature flag
 */
export const enableFeatureFlag = (flagName: string): void => {
  const flags = loadFlags();
  flags[flagName] = true;
  saveFlags(flags);
};

/**
 * Admin function to disable a feature flag
 */
export const disableFeatureFlag = (flagName: string): void => {
  const flags = loadFlags();
  flags[flagName] = false;
  saveFlags(flags);
};

/**
 * Get all flags for admin purposes
 */
export const getAllFlags = (): Record<string, boolean> => {
  return loadFlags();
};

export { FLAGS };
